import { Abas } from '@core/components/Abas';
import { CancelButton, SaveButton } from '@core/components/Buttons';
import { FormDialogContent, SimpleDialog } from '@core/components/Dialog';
import { useReduxLoading } from '@core/hooks';
import { useApiUtils } from '@core/util/apiUtils';
import { DIAS_DA_SEMANA } from '@core/util/constUtils';
import { EmpresaModais } from 'app/main/empresa/modal';
import { cancelarAlert } from 'app/services/alerts';
import { usuarioApi } from 'app/services/api/usuarioApi';
import * as Actions from 'app/store/actions';
import { GlobalContext } from 'app/store/globalcontext';
import { getEmpresaContexto } from 'app/utils/geralUtil';
import { FormikProvider, useFormik } from 'formik';
import moment from 'moment';
import React, { useContext, useEffect, useState } from 'react';
import { FaUsers } from 'react-icons/fa';
import { useDispatch } from 'react-redux';
import { usuarioModais } from '../usuarioModais';
import { TabDadosGeraisUsuario, TabRestricoesAcessoUsuario } from './components';
import TabAcessoPorEmpresa from './components/TabAcessoPorEmpresa';
import { useUsuarioValidation } from './usuarioValidation';

const usuarioAbas = {
  dadosGerais: 0,
  restricoesAcesso: 1,
  acessoPorEmpresa: 2,
};

const mapRestricaoPeriodo = (horariosAcesso, index) => {
  const diaDaSemana = DIAS_DA_SEMANA[index];

  const horarioAcesso = horariosAcesso?.filter((horario) => horario.diaDaSemana === diaDaSemana);

  if (horarioAcesso?.length) {
    return horarioAcesso[0].periodos.map((periodo) => {
      const inicio = periodo.inicio.split(':');
      const fim = periodo.fim.split(':');

      return {
        inicio: moment().set({ hours: inicio[0], minutes: inicio[1] }),
        fim: moment().set({ hours: fim[0], minutes: fim[1] }),
      };
    });
  }

  return false;
};

const getInitialValues = (usuario) => ({
  id: usuario?.id || '',
  nome: usuario?.nome || '',
  email: usuario?.email || '',
  selectedEmpresa: usuario?.selectedEmpresa || [],
  empresas: usuario?.empresas || [],
  grupoAcesso: usuario?.grupoAcesso,
  vendedor: {
    id: usuario?.vendedor?.id,
    nome: usuario?.vendedor?.pessoa?.nome,
    tipoPessoa: usuario?.vendedor?.pessoa?.tipoPessoa,
  },
  bindVendedor: !!usuario?.vendedor?.id,
  restricoes: {
    id: usuario?.restricoes?.id,
    configurarPeriodo: !!usuario?.restricoes?.periodoAcessoInicio,
    periodoAcessoInicio:
      usuario?.restricoes?.periodoAcessoInicio !== undefined
        ? moment(usuario?.restricoes?.periodoAcessoInicio)
        : usuario?.restricoes?.periodoAcessoInicio,
    periodoAcessoFim:
      usuario?.restricoes?.periodoAcessoFim !== undefined
        ? moment(usuario?.restricoes?.periodoAcessoFim)
        : usuario?.restricoes?.periodoAcessoFim,
    periodoHorarioAcesso: usuario?.restricoes?.periodoHorarioAcesso || 'QUALQUER_MOMENTO',
    horariosAcesso: [false, false, false, false, false, false, false].map((_, index) =>
      mapRestricaoPeriodo(usuario?.restricoes?.horariosAcesso, index)
    ),
    ips: usuario?.restricoes?.ips,
  },
});

const CadastroUsuarioModal = ({ onSubmit }) => {
  const [usuario] = useContext(GlobalContext).usuarioAtual;
  const [modal, setModal] = useContext(GlobalContext).modal;
  const [loading, setLoading] = useContext(GlobalContext).loading;
  const [abaSelecionada, setAbaSelecionada] = useState(usuarioAbas.dadosGerais);
  const [usuarioAtual, setUsuarioAtual] = useState({});

  const usuarioSchema = useUsuarioValidation();

  const dispatch = useDispatch();
  const apiUtils = useApiUtils(dispatch, Actions);

  useReduxLoading(loading);

  useEffect(() => {
    const buscarUsuarioLogado = async () => {
      const user = await usuarioApi.buscarUuidUsuarioLogado();
      setUsuarioAtual(user.data);
    };
    buscarUsuarioLogado();
  }, []);

  const showSuccess = (message) => {
    dispatch(Actions.showMessage({ message, variant: 'success' }));
  };

  const close = () => {
    formik.resetForm(getInitialValues());
    setAbaSelecionada(usuarioAbas.dadosGerais);
    setModal('');
  };

  const handleSubmit = async (data) => {
    let res;

    if (!formik?.dirty) {
      close();
      return;
    }

    const _usuario = JSON.parse(JSON.stringify(data));

    /**
     * Se o onSubmit for undefined então a edicao se dá pelo Meu Perfil e não
     * pela tela de usuario, pois apenas pela tela de usuário o modal possui o
     * contexto do onSubmit
     *  */
    if (onSubmit !== undefined) {
      res = await onSubmit(_usuario, { q: '', page: 0 });
    } else {
      res = await onSubmitPerfil(_usuario);
    }

    if (res) {
      close();
      if (res?.data?.id === usuarioAtual?.id) {
        const empresaIdContexto = getEmpresaContexto();
        if (!res?.data?.empresas?.some((empresa) => empresa.id === empresaIdContexto)) {
          localStorage.setItem('EC', null);
          setModal(EmpresaModais.modalSelecionarEmpresa);
        }
      }
    }
  };

  const onSubmitPerfil = async (_usuario) => {
    let res;
    let message;

    setLoading(true);

    try {
      const _data = handlePerfilData(_usuario);

      res = await usuarioApi.put(_data);
      message = 'Usuário editado com sucesso';

      showSuccess(message);
    } catch (err) {
      apiUtils.handleApiError(err);
    }

    setLoading(false);

    return res;
  };

  const handlePerfilData = (_usuario) => {
    return {
      ..._usuario,
      grupoAcessoId: _usuario?.grupoAcesso?.id,
      vendedorId: _usuario?.vendedor?.id,
      empresas: _usuario?.empresas,
      restricoes: {
        ..._usuario?.restricoes,
        periodoAcessoFim: _usuario?.restricoes?.configurarPeriodo ? _usuario?.restricoes?.periodoAcessoFim : null,
        periodoAcessoInicio: _usuario?.restricoes?.configurarPeriodo ? _usuario?.restricoes?.periodoAcessoInicio : null,
        horariosAcesso: _usuario?.restricoes?.horariosAcesso
          ?.map((horarios, index) => {
            if (!horarios) {
              return horarios;
            }

            return {
              periodos: horarios.map((periodo) => ({
                id: periodo.id,
                inicio: `${moment(periodo.inicio, [moment.ISO_8601, 'HH:mm']).format('HH:mm')}:00`,
                fim: `${moment(periodo.fim, [moment.ISO_8601, 'HH:mm']).format('HH:mm')}:59`,
              })),
              diaDaSemana: DIAS_DA_SEMANA[index],
            };
          })
          ?.filter((horarios) => !!horarios),
      },
    };
  };

  const handleClose = () => {
    if (formik.dirty) {
      cancelarAlert({
        confirmar: close,
      });
    } else {
      close();
    }
  };

  const formik = useFormik({
    validationSchema: usuarioSchema,
    initialValues: getInitialValues(usuario),
    onSubmit: handleSubmit,
    validateOnChange: true,
    validateOnBlur: true,
  });

  const { resetForm } = formik;
  useEffect(() => {
    resetForm(getInitialValues(usuario));
  }, [usuario, resetForm]);

  const hasErrorDadosGerais = (errors, touched) => {
    return (
      !!(errors.nome && touched.nome) ||
      !!(errors.email && touched.email) ||
      !!(errors.grupoAcesso && touched.grupoAcesso)
    );
  };

  const hasErrorAcessoPorEmpresa = (errors, touched) => {
    return !!(errors.selectedEmpresa && touched.selectedEmpresa);
  };

  return (
    <SimpleDialog fullScreen open={modal === usuarioModais.cadastroUsuario} onClose={handleClose} innerScroll>
      <FormikProvider value={formik}>
        <FormDialogContent
          onSubmit={formik.handleSubmit}
          fullScreen
          header={{
            title: 'Cadastro de usuário',
            icon: <FaUsers />,
            toolbar: (
              <>
                <CancelButton title="cancelar" type="button" onClick={handleClose}>
                  Cancelar
                </CancelButton>
                <SaveButton color="buttonSave" type="submit">
                  Salvar
                </SaveButton>
              </>
            ),
          }}
          contentToolbar={
            <Abas
              value={abaSelecionada}
              onChange={setAbaSelecionada}
              tabs={[
                {
                  label: 'Dados gerais',
                  hasError: () => {
                    return hasErrorDadosGerais(formik.errors, formik.touched);
                  },
                },
                {
                  label: 'Restrições de acesso',
                  hasError: () => {
                    return false;
                  },
                },
                {
                  label: 'Acesso por empresa',
                  hasError: () => {
                    return hasErrorAcessoPorEmpresa(formik.errors, formik.touched);
                  },
                },
              ]}
            />
          }
          content={
            <div className="h-full max-h-full w-full">
              {abaSelecionada === usuarioAbas.dadosGerais && <TabDadosGeraisUsuario />}
              {abaSelecionada === usuarioAbas.restricoesAcesso && <TabRestricoesAcessoUsuario />}
              {abaSelecionada === usuarioAbas.acessoPorEmpresa && <TabAcessoPorEmpresa usuarioLogado={usuarioAtual} />}
            </div>
          }
        />
      </FormikProvider>
    </SimpleDialog>
  );
};

export default CadastroUsuarioModal;
